import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ProfessionalViewMode.module.scss';
import { ProfileData } from '../../../../types';
import tagIcon from '../../../../assets/images/tag.png';
import { Alert, Snackbar } from '@mui/material';

interface ProfessionalViewModeProps {
    user: ProfileData;
    form: any;
}

export const ProfessionalViewMode: React.FC<ProfessionalViewModeProps> = ({
    user,
    form,
}) => {
    const { t } = useTranslation();

    const fullName = `${user.profile.first_name} ${user.profile.last_name}`.trim();
    const [openSnackbar, setOpenSnackbar] = React.useState(false);

    const handleNativeShare = async () => {
        const publicUrl = `${window.location.origin}/professionals/${user.profile.slug || user.profile.uid}`;
        const shareDescription = t('landing.professionalsDetail.SocialShare.shareDescription', { name: fullName });

        if (navigator.share) {
            try {
                await navigator.share({
                    title: `BodhiWell - ${fullName}`,
                    text: shareDescription,
                    url: publicUrl,
                });
            } catch (err) {
                console.error('Error sharing:', err);
            }
        } else {
            try {
                await navigator.clipboard.writeText(publicUrl);
                setOpenSnackbar(true);
            } catch (err) {
                console.error('Failed to copy: ', err);
            }
        }
    };

    const mainProfessionFormatted = (() => {
        if (!user?.profile) {
            return <p className={styles['profile-view__error']}>{t('appAdmin.EditProfessionalInfo.noData', 'No hay datos disponibles.')}</p>;
        }
        if (user.profile.public_title) {
            return user.profile.public_title;
        }
        const mp = user.profile.details?.main_specialty as any;
        if (!mp) return t('appAdmin.EditProfessionalInfo.noProfession', "Sin profesión");
        const gender = user.profile.details?.gender;

        if (gender === 'male') return mp.titulo_masculino || mp.name;
        if (gender === 'female') return mp.titulo_femenino || mp.name;
        return mp.titulo_neutro || mp.name;
    })();

    const renderTags = () => {
        const items = user.profile.specialties?.length
            ? user.profile.specialties
            : user.profile.studies?.filter(
                (s, i, arr) => !s.delete && s.specialty?.uid && arr.findIndex(a => a.specialty?.uid === s.specialty?.uid) === i
            );
        if (!items?.length) return null;

        return (
            <div className={styles['profile-view__chip-row']}>
                {items.map((item, index) => {
                    const slug = item.specialty?.dimension?.name || 'cuerpo';
                    return (
                        <span
                            key={index}
                            className={styles['passive-chip']}
                        >
                            {item.specialty?.name}
                        </span>
                    );
                })}
            </div>
        );
    };

    const details = form.details || {};
    const pricingMode = details.pricing_mode || 'single';
    const currency = details.currency === 'USD' ? 'US$' : '$U';
    const hasVirtual = !!details.virtual_link;
    const hasPresential = !!details.presential_address;

    const renderModalityText = () => {
        if (details.modality) return details.modality;

        if (hasVirtual && hasPresential) {
            return <>{t('Chat.virtual', 'Virtual')} <span className={styles['separator']}>·</span> {t('Chat.presential', 'Presencial')}</>;
        }
        if (hasVirtual) return t('Chat.virtual', 'Virtual');
        if (hasPresential) return t('Chat.presential', 'Presencial');
        return t('Chat.virtual', 'Virtual'); // fallback
    };

    const formatPrice = (price: any) => {
        return price ? Number(price).toLocaleString('es-UY') : '0';
    };

    return (
        <div className={styles['profile-view']}>
            <section className={`${styles['card']} ${styles['passive-identity-card']}`}>
                <div className={styles['passive-identity-head']}>
                    <div>
                        <h1 className={styles['passive-identity-name']}>
                            {details.name || user.profile?.first_name} {details.last_name || user.profile?.last_name}
                        </h1>
                        <p className={styles['passive-identity-role']}>
                            {mainProfessionFormatted || t('landing.professionalInfo.doctor', 'Médico')}
                        </p>
                    </div>
                    <div className={styles['passive-share']} onClick={handleNativeShare}>
                        {t('landing.professionalInfo.shareProfile', 'Compartir perfil')} ↗
                    </div>
                </div>

                <div className={styles['passive-specialties']}>
                    <div className={styles['passive-specialties-row']}>
                        <span className={styles['passive-specialties-label']}>
                            <img className={styles['passive-specialties-icon']} src={tagIcon} alt="" />
                            <span>{t('landing.professionalInfo.specialties', 'Especialidades')}</span>
                        </span>
                        {renderTags()}
                    </div>
                </div>
            </section>



            {pricingMode === 'single' ? (
                <div className={styles['pricing-reference-group']}>

                    <article className={`${styles['card']} ${styles['passive-pricing-card']} ${styles['passive-pricing-single']}`} aria-label={t('EditProfileProfessional.consultationPricing.singleValueAria', 'Valores de consulta, valor único')}>
                        <div className={styles['passive-pricing-single-metric']}>
                            <span className={styles['passive-pricing-metric-label']}>{t('EditProfileProfessional.consultationPricing.valueLabel', 'Valor')}</span>
                            <div className={styles['passive-pricing-metric-value']}>{currency} {formatPrice(details.consult_price)}</div>
                        </div>
                        <div className={styles['passive-pricing-single-metric']}>
                            <span className={styles['passive-pricing-metric-label']}>{t('EditProfileProfessional.consultationPricing.durationLabel', 'Duración')}</span>
                            <div className={styles['passive-pricing-metric-value']}>{details.duration || 60} min</div>
                        </div>
                        <div className={`${styles['passive-pricing-single-metric']} ${styles['passive-pricing-single-metric--modality']}`}>
                            <span className={styles['passive-pricing-metric-label']}>{t('Chat.modality', 'Modalidad')}</span>
                            <div className={styles['passive-pricing-metric-value']}>{renderModalityText()}</div>
                        </div>
                    </article>
                </div>
            ) : (
                <div className={styles['pricing-reference-group']}>

                    <article className={`${styles['card']} ${styles['passive-pricing-card']} ${styles['passive-pricing-split']}`} aria-label={t('EditProfileProfessional.consultationPricing.multiValueAria', 'Valores de consulta, inicial y seguimiento')}>
                        <section className={styles['passive-pricing-type']}>
                            <div className={styles['passive-pricing-type-inner']}>
                                <div className={styles['passive-pricing-type-copy']}>
                                    <h4>{t('EditProfileProfessional.consultationPricing.initialConsultTitle', 'Consulta inicial')}</h4>
                                    <p>{t('EditProfileProfessional.consultationPricing.initialConsultDesc', 'Primera entrevista, evaluación o abordaje inicial.')}</p>
                                </div>
                                <div className={styles['passive-pricing-type-metrics']}>
                                    <div className={styles['passive-pricing-type-metric']}>
                                        <span className={styles['passive-pricing-metric-label']}>{t('EditProfileProfessional.consultationPricing.valueLabel', 'Valor')}</span>
                                        <div className={styles['passive-pricing-metric-value']}>{currency} {formatPrice(details.initial_price)}</div>
                                    </div>
                                    <div className={styles['passive-pricing-type-metric']}>
                                        <span className={styles['passive-pricing-metric-label']}>{t('EditProfileProfessional.consultationPricing.durationLabel', 'Duración')}</span>
                                        <div className={styles['passive-pricing-metric-value']}>{details.initial_duration || 60} min</div>
                                    </div>
                                </div>
                            </div>
                        </section>
                        <section className={styles['passive-pricing-type']}>
                            <div className={styles['passive-pricing-type-inner']}>
                                <div className={styles['passive-pricing-type-copy']}>
                                    <h4>{t('EditProfileProfessional.consultationPricing.followupConsultTitle', 'Consulta de seguimiento')}</h4>
                                    <p>{t('EditProfileProfessional.consultationPricing.followupConsultDesc', 'Encuentros posteriores a la consulta inicial.')}</p>
                                </div>
                                <div className={styles['passive-pricing-type-metrics']}>
                                    <div className={styles['passive-pricing-type-metric']}>
                                        <span className={styles['passive-pricing-metric-label']}>{t('EditProfileProfessional.consultationPricing.valueLabel', 'Valor')}</span>
                                        <div className={styles['passive-pricing-metric-value']}>{currency} {formatPrice(details.followup_price)}</div>
                                    </div>
                                    <div className={styles['passive-pricing-type-metric']}>
                                        <span className={styles['passive-pricing-metric-label']}>{t('EditProfileProfessional.consultationPricing.durationLabel', 'Duración')}</span>
                                        <div className={styles['passive-pricing-metric-value']}>{details.followup_duration || 60} min</div>
                                    </div>
                                </div>
                            </div>
                        </section>
                        <div className={styles['passive-pricing-shared-modalities']}>
                            <div className={styles['passive-pricing-shared-inner']}>
                                <div className={styles['passive-pricing-shared-head']}>
                                    <span className={styles['passive-pricing-label']}>{t('Chat.modality', 'Modalidad')}</span>
                                    <div className={styles['passive-pricing-modality-text']}>{renderModalityText()}</div>
                                </div>
                            </div>
                        </div>
                    </article>
                </div>
            )}

            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
                    {t('landing.professionalsDetail.SocialShare.linkCopied', '¡Enlace copiado!')}
                </Alert>
            </Snackbar>
        </div>
    );
};